import type { FinancialAlert } from "./FinancialAlertBanner/FinancialAlertBanner.tsx"

export interface GroupedFinancialAlert {
  rule: string
  severity: "warning" | "critical"
  /** The alert whose basis best explains the group — planned first, since
   * that is the figure a PM is about to act on. */
  primary: FinancialAlert
  members: FinancialAlert[]
}

/**
 * One alert per rule, not one per (rule, basis).
 *
 * The engine evaluates planned and actual independently, so the same
 * condition — e.g. Contract Value Exceeded — can arrive as two alerts. The
 * sidebar shows the user one problem, not two copies of it; the group still
 * carries every basis so the expanded detail can explain both.
 */
export function groupFinancialAlerts(
  alerts: FinancialAlert[]
): GroupedFinancialAlert[] {
  const groups = new Map<string, FinancialAlert[]>()

  for (const alert of alerts) {
    const existing = groups.get(alert.rule)
    if (existing) {
      existing.push(alert)
    } else {
      groups.set(alert.rule, [alert])
    }
  }

  return Array.from(groups.entries()).map(([rule, members]) => {
    const severity = members.some((m) => m.severity === "critical")
      ? "critical"
      : "warning"
    const primary =
      members.find((m) => m.basis === "planned") ?? members[0]

    return { rule, severity, primary, members }
  })
}
